import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { RouteNames } from "@shared/types/localTypes/router";
import type { JfIconName } from "@jfrog-ba/vue-ui-core";

export enum MenuItemNames {
  Dashboard = "Dashboard",
  ImpactCenter = "Value Center",
  UsageExplorer = "Usage Explorer",
  Billing = "Billing",
  Topology = "Topology",
  Security = "Security",
  Users = "Manage Users",
  Settings = "Settings",
  JPD = "JPD",
  Licenses = "Licenses",
  AuthenticatedRelease = "Authenticated Release",
  Sandbox = "Sandbox",
  SupportContacts = "Support Contacts",
  NotificationCenter = "Notification Center",
  ActivityLog = "Audit Logs",
  SupportTickets = "Support Tickets",
  ThreatIntelligence = "Threat Intelligence",
}

export enum MenuHeaderNames {
  Dashboard = "Dashboard",
  ImpactCenter = "Value Center",
  Usage = "Usage",
  Billing = "Billing",
  Topology = "Topology",
  Security = "Security",
  Users = "Manage Users",
  Subscription = "Subscription",
  Settings = "Settings",
  JPD = "JPDs",
  Licenses = "Licenses",
  AuthenticatedRelease = "Authenticated Release",
  Upgrade = "Upgrade",
  AllSubscriptions = "All Subscriptions",
  Sandbox = "Sandbox",
  SupportContacts = "Support Contacts",
  SupportTickets = "Support Tickets",
  ThreatIntelligence = "Threat Intelligence",
}

export interface MenuItem {
  name: MenuItemNames;
  routeName: RouteNames;
  /** @jfrog-ba/vue-ui-core `JfIcon` `name` (literal jf-*) */
  jfIcon: JfIconName;
  menuIndex: string;
  disabled: boolean;
  hidden: boolean;
  isNew: boolean;
}

export const useNavStore = defineStore("navigation", () => {
  // State
  let menuIndex = 0;
  const _activeIndex = ref<null | string>(null);
  const _viewTabName = ref<null | string>(null);
  const _menuItems = ref<MenuItem[]>([
    {
      name: MenuItemNames.Dashboard,
      routeName: RouteNames.Dashboard,
      jfIcon: "jf-dashboard",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.ImpactCenter,
      routeName: RouteNames.ImpactCenter,
      jfIcon: "jf-impact",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.UsageExplorer,
      routeName: RouteNames.UsageExplorer,
      jfIcon: "jf-my-jfrog",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.Billing,
      routeName: RouteNames.Billing,
      jfIcon: "jf-receipt",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.Topology,
      routeName: RouteNames.Topology,
      jfIcon: "jf-topology",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.Security,
      routeName: RouteNames.Security,
      jfIcon: "jf-shield-check",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.Users,
      routeName: RouteNames.Users,
      jfIcon: "jf-add-users",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.SupportTickets,
      routeName: RouteNames.SupportTickets,
      jfIcon: "jf-chat",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.NotificationCenter,
      routeName: RouteNames.NotificationCenter,
      jfIcon: "jf-bell",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.ActivityLog,
      routeName: RouteNames.ActivityLog,
      jfIcon: "jf-pencil-clock",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.SupportContacts,
      routeName: RouteNames.SupportContacts,
      jfIcon: "jf-support",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.Settings,
      routeName: RouteNames.Settings,
      jfIcon: "jf-settings",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.JPD,
      routeName: RouteNames.JPDs,
      jfIcon: "jf-cloud-sync",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.Licenses,
      routeName: RouteNames.Licenses,
      jfIcon: "jf-doc",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.AuthenticatedRelease,
      routeName: RouteNames.AuthenticatedRelease,
      jfIcon: "jf-large-key",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: true,
    },
    {
      name: MenuItemNames.ThreatIntelligence,
      routeName: RouteNames.ThreatIntelligence,
      jfIcon: "jf-shield-critical-outline",
      menuIndex: `${menuIndex++}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
    {
      name: MenuItemNames.Sandbox,
      routeName: RouteNames.Sandbox,
      jfIcon: "jf-integrations",
      menuIndex: `${menuIndex}`,
      disabled: true,
      hidden: true,
      isNew: false,
    },
  ]);
  const _isMenuOpened = ref<boolean>(true);

  // Getters
  const activeIndex = computed(() => _activeIndex.value);
  const activeMenu = computed(() =>
    _menuItems.value.find((menu) => menu.menuIndex === _activeIndex.value),
  );
  const menuItems = computed(() => _menuItems.value);
  const isMenuOpened = computed(() => _isMenuOpened.value);
  const viewTabName = computed(() => _viewTabName.value);

  // Actions
  const setActiveMenu = (menuName: MenuItemNames) => {
    const relevantMenu = _menuItems.value.find((m) => m.name === menuName);
    _activeIndex.value = relevantMenu ? relevantMenu.menuIndex : null;
  };

  const clearActiveMenu = () => {
    _activeIndex.value = null;
  };

  const setViewTabName = (name: string) => {
    _viewTabName.value = name;
  };

  const clearViewTabName = () => {
    _viewTabName.value = null;
  };

  const setMenuItemVisibility = (menuName: MenuItemNames, visible: boolean) => {
    const relevantMenu = _menuItems.value.find(
      (menuItem) => menuItem.name === menuName,
    );
    if (!relevantMenu) {
      return;
    }
    relevantMenu.hidden = !visible;
  };

  const setMenuItemDisabled = (menuName: MenuItemNames, disabled: boolean) => {
    const relevantMenu = _menuItems.value.find(
      (menuItem) => menuItem.name === menuName,
    );
    if (!relevantMenu) {
      return;
    }
    relevantMenu.disabled = disabled;
  };

  const setMenuItemNew = (menuName: MenuItemNames, isNew: boolean) => {
    const relevantMenu = _menuItems.value.find(
      (menuItem) => menuItem.name === menuName,
    );
    if (!relevantMenu) {
      return;
    }
    relevantMenu.isNew = isNew;
  };

  const setMenuOpen = (isOpen: boolean) => {
    _isMenuOpened.value = isOpen;
  };

  return {
    activeIndex,
    activeMenu,
    menuItems,
    isMenuOpened,
    viewTabName,
    setActiveMenu,
    clearActiveMenu,
    setViewTabName,
    clearViewTabName,
    setMenuItemVisibility,
    setMenuItemDisabled,
    setMenuItemNew,
    setMenuOpen,
  };
});
